import React from "react";
import dayjs from "dayjs";
import {
    LineChart as RLineChart,
    Line,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    DefaultLegendContent,
} from "recharts";
import { ChevronRight } from "lucide-react";

export interface MonthlySummary {
    month: string;
    sales: number;
    total_advertising: number;
    visit: number;
}

const LEGEND_ORDER = ["sales", "total_advertising", "visit"];

interface MonthlyChartProps {
    data?: MonthlySummary[] | null;
    loading?: boolean;
    emptyLabel?: string;
}

function parseMonthToDayjs(m: string) {
    const s = String(m || "").trim();
    if (!s) return null;

    // "YYYY-MM" -> "YYYY-MM-01"
    if (/^\d{4}-\d{2}$/.test(s)) return dayjs(`${s}-01`);
    // "YYYY/MM" -> "YYYY-MM-01"
    if (/^\d{4}\/\d{2}$/.test(s)) return dayjs(s.replace("/", "-") + "-01");
    // "YYYY-MM-DD"
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return dayjs(s).startOf("month");

    // 最後の手段（parseできれば month start）
    const d = dayjs(s);
    return d.isValid() ? d.startOf("month") : null;
}

const MonthlyChart: React.FC<MonthlyChartProps> = ({
    data,
    loading = false,
    emptyLabel = "データがありません",
}) => {
    const safeData = Array.isArray(data) ? data : [];

    // ✅ 当月〜過去13ヶ月（当月含む）に絞る
    const endMonth = dayjs().startOf("month");          // 当月
    const startMonth = endMonth.subtract(12, "month");  // 12ヶ月前の月初（= 13ヶ月分）

    const safeData13 = safeData
        .map((r) => {
            const dm = parseMonthToDayjs(r.month);
            return { row: r, dm };
        })
        .filter((x) => x.dm && (x.dm.isSame(startMonth) || x.dm.isAfter(startMonth)) && (x.dm.isSame(endMonth) || x.dm.isBefore(endMonth)))
        .sort((a, b) => (a.dm!.valueOf() - b.dm!.valueOf()))
        .map((x) => x.row);

    const hasData = safeData13.length > 0;

    return (
        <div className="rounded-2xl border bg-white p-5 shadow-sm xl:col-span-2">
            <div className="mb-3 items-center justify-between">
                <div>
                    <div className="mb-3 text-xs font-semibold text-slate-800">月次 推移</div>
                    <div className="text-xs text-slate-500">総売上・広告費・アクセス数の推移</div>
                </div>
            </div>

            {!hasData ? (
                <div className="text-sm text-slate-500">{loading ? "読み込み中..." : emptyLabel}</div>
            ) : (
                <div className="mt-4 h-72 w-full">
                    <ResponsiveContainer width="100%" height="100%">
                        <RLineChart
                            data={safeData13} // ✅ ここだけ差し替え
                            margin={{ top: 8, right: 16, left: 8, bottom: 8 }}
                            barCategoryGap="20%"
                            barGap={4}
                        >
                            <CartesianGrid strokeDasharray="3 3" />

                            <XAxis
                                dataKey="month"
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                interval="preserveStartEnd"
                                padding={{ left: 30, right: 40 }}
                            />

                            <YAxis
                                yAxisId="left"
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                tickFormatter={(v: number) => `${Number(v).toLocaleString()}円`}
                            />

                            <YAxis
                                yAxisId="right"
                                orientation="right"
                                tick={{ fill: "#64748b", fontSize: 11 }}
                                tickFormatter={(v: number) => `${Number(v).toLocaleString()}人`}
                            />

                            <Tooltip
                                formatter={(value, name, _item, _index) => {
                                    const num = Math.round(Number(value) || 0);
                                    const key = String(name ?? "");

                                    // key が dataKey の時も、表示名の時も対応
                                    if (key === "sales" || key === "総売上") return [`${num.toLocaleString()}円`, "総売上"];
                                    if (key === "total_advertising" || key === "広告費") return [`${num.toLocaleString()}円`, "広告費"];
                                    if (key === "visit" || key === "アクセス数") return [`${num.toLocaleString()}人`, "アクセス数"];

                                    // ✅ 最後も必ず num を返す（valueは返さない）
                                    return [num.toLocaleString(), key];
                                }}
                                labelFormatter={(label) => `${label}`}
                            />

                            <Bar
                                yAxisId="left"
                                dataKey="total_advertising"
                                name="広告費"
                                fill="#ec4899"
                                barSize={20}
                                radius={[2, 2, 0, 0]}
                            />

                            <Bar
                                yAxisId="left"
                                dataKey="sales"
                                name="総売上"
                                fill="#3b82f6"
                                barSize={20}
                                radius={[2, 2, 0, 0]}
                            />

                            <Line
                                yAxisId="right"
                                type="linear"
                                dataKey="visit"
                                name="アクセス数"
                                stroke="#f97316"
                                strokeWidth={2}
                                dot={{ r: 2 }}
                            />

                            <Legend
                                wrapperStyle={{ fontSize: 11 }}
                                content={(props: any) => {
                                    const payload = props?.payload || [];
                                    const sorted = [...payload].sort((a: any, b: any) => {
                                        const ai = LEGEND_ORDER.indexOf(a.dataKey);
                                        const bi = LEGEND_ORDER.indexOf(b.dataKey);
                                        return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi);
                                    });
                                    return <DefaultLegendContent {...props} payload={sorted} />;
                                }}
                            />
                        </RLineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
};

export default MonthlyChart;